// MEDDPICC qualification panel. Renders the auxiliary, NON-SCORING qualification scan into its own
// box. This is deliberately separate from the scoring results (results.ts): the MEDDPICC findings
// never touch the final score — they are a qualification signal for the manager. The panel is
// data-driven and self-contained, so wherever the host adds a container element it lights up.

import { setText } from '../lib/escape.js';
import type { MeddpiccReport, MeddpiccElement } from '../lib/types.js';

const STATUS_LABEL: Record<MeddpiccElement['status'], string> = {
  strong: 'Strong',
  weak: 'Weak',
  missing: 'Missing',
};

/** Render the MEDDPICC scan into `root` (clears it first). No-op if root is null. */
export function renderMeddpicc(root: HTMLElement | null, report: MeddpiccReport): void {
  if (!root) return;
  root.innerHTML = '';
  const list = document.createElement('ul');
  list.className = 'meddpicc-list';
  for (const el of report.elements) {
    list.appendChild(elementRow(el));
  }
  root.appendChild(list);
}

/** Empty the MEDDPICC panel (used on import / reset / clear / stop). No-op if root is null. */
export function clearMeddpicc(root: HTMLElement | null): void {
  if (root) root.innerHTML = '';
}

/** Why a scan failed → a short, honest line for the panel (so it never just sits blank). */
const UNAVAILABLE_MESSAGE: Record<'model_error' | 'leak' | 'unparseable', string> = {
  model_error: 'Qualification scan unavailable, the AI service did not respond. Re-run to try again.',
  leak: 'Qualification scan was withheld by the safety gate for this transcript.',
  unparseable: 'Qualification scan unavailable, the result came back incomplete. Re-run to try again.',
};

/**
 * Render an explicit "unavailable" state when the MEDDPICC scan could not complete. This replaces the
 * old silent-empty-box behavior: the manager now sees that the scan failed (and why), distinct from a
 * call that genuinely had no qualification evidence. Never affects the score.
 */
export function renderMeddpiccUnavailable(
  root: HTMLElement | null,
  reason: 'model_error' | 'leak' | 'unparseable',
): void {
  if (!root) return;
  root.innerHTML = '';
  const note = document.createElement('p');
  note.className = 'meddpicc-unavailable';
  setText(note, UNAVAILABLE_MESSAGE[reason] ?? UNAVAILABLE_MESSAGE.model_error);
  root.appendChild(note);
}

function elementRow(el: MeddpiccElement): HTMLLIElement {
  const li = document.createElement('li');
  li.className = `meddpicc-item meddpicc-${el.status}`;
  li.dataset.key = el.key;

  const head = document.createElement('div');
  head.className = 'meddpicc-head';

  const letter = document.createElement('span');
  letter.className = 'meddpicc-letter';
  setText(letter, el.letter);

  const label = document.createElement('span');
  label.className = 'meddpicc-label';
  setText(label, el.label);

  const status = document.createElement('span');
  status.className = `meddpicc-status meddpicc-status-${el.status}`;
  setText(status, STATUS_LABEL[el.status]);

  head.append(letter, label, status);
  li.appendChild(head);

  if (el.note) {
    const note = document.createElement('div');
    note.className = 'meddpicc-note';
    setText(note, el.note);
    li.appendChild(note);
  }
  if (el.evidence) {
    const ev = document.createElement('blockquote');
    ev.className = 'meddpicc-evidence';
    setText(ev, el.evidence);
    li.appendChild(ev);
  }
  return li;
}
